/**
 * The cover animation: RWY 17 seen from the approach, at night.
 *
 * Ported from the single-file demo without changing a number. The geometry is
 * an Annex 14 layout in perspective — piano keys, aiming point, touchdown zone,
 * centreline stripes — lit as a CAT III runway is lit: white edges going yellow
 * through the last 600 m, centreline white then alternating red and white then
 * red, PAPI showing two white two red on the glide path, a green wing bar at
 * the threshold and red at the end.
 *
 * Over the sky sits the thing the platform is actually selling: a sweep with a
 * decaying trail, contacts that light as the beam passes and fade behind it,
 * and a faint node network. It runs on requestAnimationFrame off Date.now(), so
 * it is time-driven rather than frame-driven and looks the same on any monitor.
 */
import { hiCanvas } from "../lib/canvas";
import { t } from "../../i18n";

export function drawGateArt(c: HTMLCanvasElement): void {
  const hi = hiCanvas(c, 300);
  if (!hi) return;
  const { g, w, h } = hi;
  const secs = Date.now() / 1000;
  const hz = h * 0.44,
    cx = w / 2; /* horizon and the runway centre */

  /* --- sky and ground ----------------------------------------------------- */
  const sky = g.createLinearGradient(0, 0, 0, hz);
  sky.addColorStop(0, "#04080E");
  sky.addColorStop(1, "#0A1826");
  g.fillStyle = sky;
  g.fillRect(0, 0, w, hz);
  const gnd = g.createLinearGradient(0, hz, 0, h);
  gnd.addColorStop(0, "#08111A");
  gnd.addColorStop(1, "#050B12");
  g.fillStyle = gnd;
  g.fillRect(0, hz, w, h - hz);
  g.strokeStyle = "rgba(120,150,175,.22)";
  g.lineWidth = 1;
  g.beginPath();
  g.moveTo(0, hz + 0.5);
  g.lineTo(w, hz + 0.5);
  g.stroke();

  /* --- perspective -------------------------------------------------------- */
  /* d runs 0 at the threshold nearest the viewer to 1 at the far end */
  const NEAR = h * 0.8,
    FAR = hz + 5;
  const py = (d: number) => FAR + (NEAR - FAR) * Math.pow(1 - d, 2.0);
  const halfNear = w * 0.255,
    halfFar = 4;
  const hw = (d: number) => halfFar + (halfNear - halfFar) * Math.pow(1 - d, 2.0);
  /* off is −1..1 across the width */
  const L = (d: number, off: number): [number, number] => [cx + off * hw(d), py(d)];
  const dot = (x: number, y: number, r: number, col: string, glow?: number) => {
    g.beginPath();
    g.arc(x, y, r, 0, 6.284);
    g.fillStyle = col;
    g.shadowBlur = glow == null ? r * 3.4 : glow;
    g.shadowColor = col;
    g.fill();
    g.shadowBlur = 0;
  };
  const bar = (d: number, o1: number, o2: number, col: string, th?: number) => {
    const [x1, y1] = L(d, o1),
      [x2] = L(d, o2);
    g.strokeStyle = col;
    g.lineWidth = th || Math.max(1, 4.5 * (1 - d) * (1 - d) + 0.6);
    g.shadowBlur = 7;
    g.shadowColor = col;
    g.beginPath();
    g.moveTo(x1, y1);
    g.lineTo(x2, y1);
    g.stroke();
    g.shadowBlur = 0;
  };

  /* --- the pavement ------------------------------------------------------- */
  g.beginPath();
  g.moveTo(...L(0, -1));
  g.lineTo(...L(1, -1));
  g.lineTo(...L(1, 1));
  g.lineTo(...L(0, 1));
  g.closePath();
  const pav = g.createLinearGradient(0, py(1), 0, py(0));
  pav.addColorStop(0, "#0E1B25");
  pav.addColorStop(1, "#16242F");
  g.fillStyle = pav;
  g.fill();
  /* shoulders */
  [-1, 1].forEach((s) => {
    g.beginPath();
    g.moveTo(...L(0, s));
    g.lineTo(...L(1, s));
    g.lineTo(...L(1, s * 1.16));
    g.lineTo(...L(0, s * 1.16));
    g.closePath();
    g.fillStyle = "rgba(30,45,58,.55)";
    g.fill();
  });

  /* --- markings, all white, Annex 14 layout -------------------------------- */
  const white = "rgba(240,246,250,.92)";
  /* threshold bars — the piano keys */
  for (let i = -6; i <= 6; i++) {
    if (i === 0) continue;
    const o = i * 0.135;
    g.beginPath();
    g.moveTo(...L(0.018, o - 0.042));
    g.lineTo(...L(0.062, o - 0.042));
    g.lineTo(...L(0.062, o + 0.042));
    g.lineTo(...L(0.018, o + 0.042));
    g.closePath();
    g.fillStyle = white;
    g.fill();
  }
  /* aiming point — two heavy blocks at 400 m */
  [-0.34, 0.34].forEach((o) => {
    g.beginPath();
    g.moveTo(...L(0.325, o - 0.06));
    g.lineTo(...L(0.415, o - 0.06));
    g.lineTo(...L(0.415, o + 0.06));
    g.lineTo(...L(0.325, o + 0.06));
    g.closePath();
    g.fillStyle = white;
    g.fill();
  });
  /* touchdown zone markings — paired bars at 150 m intervals */
  (
    [
      [0.15, 3],
      [0.235, 2],
      [0.46, 2],
      [0.56, 1],
    ] as [number, number][]
  ).forEach(([d, n]) => {
    for (let k = 0; k < n; k++) {
      [-1, 1].forEach((s) => {
        const o = s * (0.2 + k * 0.075);
        g.beginPath();
        g.moveTo(...L(d, o - 0.022));
        g.lineTo(...L(d + 0.045, o - 0.022));
        g.lineTo(...L(d + 0.045, o + 0.022));
        g.lineTo(...L(d, o + 0.022));
        g.closePath();
        g.fillStyle = white;
        g.fill();
      });
    }
  });
  /* centreline — 30 m stripes, 20 m gaps */
  for (let i = 0; i < 22; i++) {
    const d1 = 0.115 + i * 0.041,
      d2 = d1 + 0.02;
    if (d2 > 0.985) break;
    g.beginPath();
    g.moveTo(...L(d1, -0.018));
    g.lineTo(...L(d2, -0.018));
    g.lineTo(...L(d2, 0.018));
    g.lineTo(...L(d1, 0.018));
    g.closePath();
    g.fillStyle = `rgba(240,246,250,${0.92 - i * 0.024})`;
    g.fill();
  }
  /* the designator, over the pavement */
  g.save();
  g.font = "700 22px ui-monospace,Menlo,monospace";
  g.fillStyle = "rgba(240,246,250,.90)";
  g.textAlign = "center";
  g.fillText("17", cx, py(0.075) + 8);
  g.restore();
  /* side stripes */
  [-0.93, 0.93].forEach((o) => {
    g.beginPath();
    g.moveTo(...L(0.01, o - 0.02));
    g.lineTo(...L(0.99, o - 0.012));
    g.lineTo(...L(0.99, o + 0.012));
    g.lineTo(...L(0.01, o + 0.02));
    g.closePath();
    g.fillStyle = "rgba(240,246,250,.55)";
    g.fill();
  });

  /* --- approach lighting, CAT III: white barrettes, red side rows ---------- */
  const apTop = py(0) + 4,
    apGap = (h - apTop) / 5.0;
  for (let i = 1; i <= 5; i++) {
    const yy = apTop + i * apGap;
    if (yy > h - 3) break;
    const sc = 1 + i * 0.15;
    const bw = hw(0) * 0.1 * sc;
    /* centreline barrette */
    g.strokeStyle = "rgba(255,255,255,.95)";
    g.lineWidth = 3.4;
    g.shadowBlur = 12;
    g.shadowColor = "#FFFFFF";
    g.beginPath();
    g.moveTo(cx - bw, yy);
    g.lineTo(cx + bw, yy);
    g.stroke();
    g.shadowBlur = 0;
    /* the 300 m crossbar */
    if (i === 3) {
      g.strokeStyle = "rgba(255,255,255,.9)";
      g.lineWidth = 3;
      g.shadowBlur = 12;
      g.shadowColor = "#FFFFFF";
      g.beginPath();
      g.moveTo(cx - hw(0) * 0.4 * sc, yy);
      g.lineTo(cx + hw(0) * 0.4 * sc, yy);
      g.stroke();
      g.shadowBlur = 0;
    }
    /* red side row barrettes */
    if (i <= 3)
      [-1, 1].forEach((s) => {
        const x0 = cx + s * hw(0) * 0.3 * sc;
        g.strokeStyle = "rgba(255,64,58,.95)";
        g.lineWidth = 3.2;
        g.shadowBlur = 11;
        g.shadowColor = "#FF4D45";
        g.beginPath();
        g.moveTo(x0, yy);
        g.lineTo(x0 + s * bw * 0.9, yy);
        g.stroke();
        g.shadowBlur = 0;
      });
  }

  /* --- the lights themselves ---------------------------------------------- */
  /* threshold: green wing bar */
  for (let i = -8; i <= 8; i++) {
    const [x, y] = L(0.004, i * 0.118);
    dot(x, y, 2.9, "rgba(46,235,124,.98)", 14);
  }
  /* runway end: red */
  for (let i = -5; i <= 5; i++) {
    const [x, y] = L(0.995, i * 0.19);
    dot(x, y, 1.5, "rgba(255,64,58,.95)", 8);
  }
  /* edge lights — white, and yellow through the last 600 m */
  for (let i = 0; i <= 30; i++) {
    const d = i / 30;
    const caution = d > 0.74; /* the caution zone */
    const col = caution ? "rgba(255,206,84,.98)" : "rgba(248,251,255,.96)";
    const r = 1.0 + 2.6 * Math.pow(1 - d, 2.1);
    const flick = 0.86 + 0.14 * Math.sin(secs * 2.2 + i * 0.9);
    [-1.02, 1.02].forEach((s) => {
      const [x, y] = L(d, s);
      g.globalAlpha = flick;
      dot(x, y, r, col, r * 4);
      g.globalAlpha = 1;
    });
  }
  /* centreline lights — white, then alternating red and white, then red */
  for (let i = 0; i <= 44; i++) {
    const d = i / 44;
    let col: string;
    if (d < 0.6) col = "rgba(248,251,255,.98)"; /* white */
    else if (d < 0.85) col = i % 2 ? "rgba(255,64,58,.95)" /* alternating */ : "rgba(248,251,255,.98)";
    else col = "rgba(255,64,58,.95)"; /* the last 300 m */
    const [x, y] = L(d, 0);
    dot(x, y, 0.9 + 2.1 * Math.pow(1 - d, 2.1), col);
  }
  /* touchdown zone lights — white barrettes, both sides, first 900 m */
  for (let i = 0; i < 11; i++) {
    const d = 0.05 + i * 0.052;
    [-1, 1].forEach((s) => {
      for (let k = 0; k < 2; k++) {
        const [x, y] = L(d, s * (0.115 + k * 0.062));
        dot(x, y, 0.8 + 1.6 * Math.pow(1 - d, 2.1), "rgba(248,251,255,.92)");
      }
    });
  }
  /* PAPI, left side: two white two red — on the glide path */
  for (let i = 0; i < 4; i++) {
    const [x, y] = L(0.245, -1.3 - i * 0.1);
    dot(x, y, 3.4, i < 2 ? "rgba(255,64,58,.98)" : "rgba(248,251,255,.98)", 15);
  }
  /* taxiway: green centreline turning off, blue edges */
  for (let i = 0; i < 9; i++) {
    const d = 0.62 + i * 0.016;
    const [x, y] = L(d, -1.1 - i * 0.09);
    dot(x, y, 1.0 + 1.1 * (1 - i / 9), "rgba(46,235,124,.9)", 7);
  }
  [-1.3, -1.55].forEach((o) => {
    for (let i = 0; i < 5; i++) {
      const [x, y] = L(0.7 + i * 0.03, o - i * 0.02);
      dot(x, y, 1.1, "rgba(74,158,255,.9)", 7);
    }
  });
  /* stop bar at the holding position — red, across the taxiway */
  bar(0.615, -1.22, -0.98, "rgba(255,64,58,.9)", 2.2);

  /* --- the model watching it ---------------------------------------------- */
  const rx = cx,
    ry = hz * 0.52,
    R = Math.min(w * 0.36, hz * 0.98);
  /* rings */
  for (let i = 1; i <= 3; i++) {
    g.beginPath();
    g.ellipse(rx, ry, (R * i) / 3, ((R * i) / 3) * 0.38, 0, 0, 6.284);
    g.strokeStyle = `rgba(63,208,122,${0.3 - i * 0.05})`;
    g.lineWidth = 1.2;
    g.stroke();
  }
  g.beginPath();
  g.moveTo(rx - R, ry);
  g.lineTo(rx + R, ry);
  g.strokeStyle = "rgba(63,208,122,.10)";
  g.stroke();
  g.beginPath();
  g.moveTo(rx, ry - R * 0.38);
  g.lineTo(rx, ry + R * 0.38);
  g.stroke();
  /* the sweep, with a trail behind it */
  const sa = (secs * 0.72) % (Math.PI * 2);
  for (let k = 0; k < 26; k++) {
    const a = sa - k * 0.045;
    const al = 0.46 * (1 - k / 26);
    g.beginPath();
    g.moveTo(rx, ry);
    g.lineTo(rx + Math.cos(a) * R, ry + Math.sin(a) * R * 0.38);
    g.strokeStyle = `rgba(63,208,122,${al})`;
    g.lineWidth = 2.4;
    g.stroke();
  }
  /* the leading edge, bright */
  g.beginPath();
  g.moveTo(rx, ry);
  g.lineTo(rx + Math.cos(sa) * R, ry + Math.sin(sa) * R * 0.38);
  g.strokeStyle = "rgba(160,255,200,.98)";
  g.lineWidth = 2.8;
  g.shadowBlur = 20;
  g.shadowColor = "#3FD07A";
  g.stroke();
  g.shadowBlur = 0;
  /* contacts, lighting as the beam passes and fading after */
  const blips: [number, number][] = [
    [0.42, 0.9],
    [0.74, 2.3],
    [0.3, 3.7],
    [0.86, 4.6],
    [0.56, 5.5],
    [0.64, 1.7],
  ];
  blips.forEach(([rr, aa]) => {
    const diff = (sa - aa + Math.PI * 2) % (Math.PI * 2);
    const life = Math.max(0, 1 - diff / 1.5);
    if (life <= 0.02) return;
    const bx = rx + Math.cos(aa) * R * rr,
      by = ry + Math.sin(aa) * R * 0.38 * rr;
    dot(bx, by, 2.0 + life * 2.2, `rgba(120,255,175,${0.3 + life * 0.65})`, 12);
    if (life > 0.55) {
      g.beginPath();
      g.arc(bx, by, (1 - life) * 22, 0, 6.284);
      g.strokeStyle = `rgba(120,255,175,${(life - 0.55) * 0.7})`;
      g.lineWidth = 1;
      g.stroke();
    }
  });
  /* the network, faint, above the sweep */
  const nodes: { x: number; y: number }[] = [];
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2 + secs * 0.05;
    const r = R * 0.55 + R * 0.55 * (((i * 37) % 11) / 11);
    nodes.push({ x: rx + Math.cos(a) * r, y: ry + Math.sin(a) * r * 0.34 });
  }
  g.lineWidth = 1;
  nodes.forEach((n, i) =>
    nodes.slice(i + 1).forEach((m) => {
      const dd = Math.hypot(n.x - m.x, n.y - m.y);
      if (dd > 88) return;
      g.strokeStyle = `rgba(76,155,232,${0.16 * (1 - dd / 88)})`;
      g.beginPath();
      g.moveTo(n.x, n.y);
      g.lineTo(m.x, m.y);
      g.stroke();
    }),
  );
  nodes.forEach((n, i) => {
    const p = 0.5 + 0.5 * Math.sin(secs * 1.8 + i);
    dot(n.x, n.y, 1.2 + p * 1.2, `rgba(159,208,255,${0.3 + p * 0.4})`, 8);
  });

  /* --- the label ----------------------------------------------------------- */
  g.font = "700 8.5px ui-monospace,Menlo,monospace,'IBM Plex Sans Arabic'";
  g.fillStyle = "rgba(140,163,179,.72)";
  g.fillText(t("RWY 17/35 · CAT III · AGL SERVICEABLE"), 10, 16);
  g.textAlign = "right";
  g.fillStyle = "rgba(63,208,122,.72)";
  g.fillText(t("PREDICTIVE MODEL · SCANNING"), w - 10, 16);
  g.textAlign = "left";
}
